import React, {
  Fragment,
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react';
import { message } from 'antd';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import 'antd/dist/antd.min.css';
import './style.css';
import './Application.scss';
import { useDropzone } from 'react-dropzone';
import states from '../../../../Data/state.json';
import lgas from '../../../../Data/lga.json';
import RegistrationFail from '../../Components/ConfirmationSlip/RegistrationFail/RegistrationFail';
import { getAgeFromYear } from '../../../../Services/getAgeFromYear';
import { AllowAccessToApplication } from '../../../../Helpers';
import { BASE_URL_ADDR } from '../../../../Constants/BaseURL';
import { Base64AppendString } from '../../../../Constants/Base64AppendString';
import { useLocation, useNavigate } from 'react-router-dom';
import StageTag from '../../../../Components/StageTag/StageTag';
import ApplicationStage2 from './Steps/ApplicationStage2';
import ApplicationStage4 from './Steps/ApplicationStage4';
import ReviewStage from './Steps/ReviewAllStage';
import applicantsService from '../../../../Services/applicantsService';
import toast, { Toaster } from 'react-hot-toast';
import { SHA256 } from 'crypto-js';
import CloudAssert from '../../../../Hooks/loadAssert';
import useManifest from '../../../../Hooks/useManifest';

type DocumentsType = {
  ssce: File | string;
  ssce2: File | string;
  birthCertificate: File | string;
  lgaCertificate: File | string;
  professionalCertificate: File | string;
  degreeType: File | string;
  firstDegreeType: File | string;
  [key: string]: any;
};

interface InstitutionRow {
  degreeTitle: string;
  institutionName: string;
  degreeCertificateNumber: string;
  degreeAwarded: string;
  graduationYear: string;
  first_graduationYear: string;
  first_degreeAwarded: string;
  first_institutionName: string;
  first_degreeCertificateNumber: string;
  first_degreeTitle: string;
  other_degree?: string;
  other_degreeAwarded?: string;
  other_graduationYear?: string;
}

const emptyFormData = {
  nin: '',
  surname: '',
  firstname: '',
  middlename: '',
  gender: '',
  maritalStatus: '',
  dob: '',
  contactPhone: '+234',
  nationality: '',
  stateOfPermanent: '',
  lgaOfPermanent: '',
  height: '',
  streetAddressOfPermanent: '',
  phoneno: '+234',
  phone1: '+234',
  phone2: '+234',
  email: '',
  postalAddress: '',
  stateOfResidence: '',
  lgaOfResidence: '',
  streetAddressOfResidence: '',
  nextOfKinName: '',
  nextOfKinPhone: '',
  nextOfKinEmail: '',
  nextOfKinGender: '',
  nextOfKinRelationship: '',
  resume: '',
  waec: false,
  gce: false,
  neco: false,
  nabteb: false,
  birthCertificate: false,
  lgaCertificate: false,
  professionalCertificate: false,
  firstSittingRegNo: '',
  firstSitting: {
    subject1: 'Mathematics',
    grade1: '',
    subject2: 'English',
    grade2: '',
    subject3: '',
    grade3: '',
    subject4: '',
    grade4: '',
    subject5: '',
    grade5: '',
    subject6: '',
    grade6: '',
    subject7: '',
    grade7: '',
    subject8: '',
    grade8: '',
    subject9: '',
    grade9: '',
  },
  secondSitting: {
    subject1: 'Mathematics',
    grade1: '',
    subject2: 'English',
    grade2: '',
    subject3: '',
    grade3: '',
    subject4: '',
    grade4: '',
    subject5: '',
    grade5: '',
    subject6: '',
    grade6: '',
    subject7: '',
    grade7: '',
    subject8: '',
    grade8: '',
    subject9: '',
    grade9: '',
  },
  secondSittingRegNo: '',
};

const documentsMapper = (type: string): string => {
  switch (type) {
    case 'ssce':
      return 'SSCE';
    case 'ssce2':
      return 'SSCE2';
    case 'birthCertificate':
      return 'Birth Certificate/Age Declaration';
    case 'lgaCertificate':
      return 'LGA/ lga certificate ';
    case 'firstDegre':
      return 'First Degree Certificate';
    case 'professionalCertificate':
      return 'Professional certificate';
    default:
      return '';
  }
};

const emptyDocuments: DocumentsType = {
  ssce: '',
  ssce2: '',
  birthCertificate: '',
  lgaCertificate: '',
  professionalCertificate: '',
  degreeType: '',
  firstDegreeType: '',
};

const loaders = {
  submit: false,
  nimc: false,
  jamb: false,
  step1: false,
  step2: false,
};

const Application = () => {
  const location = useLocation();
  const { dataset, photo, metadata, uuid }: any = location?.state || {};
  const resData = dataset;

  const newPhotoInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // State
  const [formData, setFormData] = useState(emptyFormData);
  const [documents, setDocuments] = useState<DocumentsType>(emptyDocuments);
  const [loading, setLoading] = useState(loaders);
  const [consent, setConsent] = useState(false);
  const [ninValidated, setNinValidated] = useState(false);
  const [nimcData, setNimcData] = useState<any>();
  const [ssce] = useState<any>(null); // Kept but currently unused in update logic
  const [ssceLoading] = useState<boolean>(false); // Unused in provided code but passed to Stage4

  const [documentError, setDocumentError] = useState<string>('');
  const [resultError, setResultError] = useState<string>('');
  const [examNoError, setExamNoError] = useState<string>('');
  const [boxErrorMessage, setBoxErrorMessage] = useState<string>('');
  const [radioForm, setRadioForm] = useState<string>('');

  const [institutionRow, setInstitutionRow] = useState<InstitutionRow[]>([
    {
      degreeTitle: '',
      institutionName: '',
      degreeCertificateNumber: '',
      degreeAwarded: '',
      graduationYear: '',
      first_graduationYear: '',
      first_degreeAwarded: '',
      first_institutionName: '',
      first_degreeCertificateNumber: '',
      first_degreeTitle: '',
    },
  ]);

  const [currentSitting, setCurrentSitting] = useState<string>('');
  const [sittings, setSittings] = useState<number>(0);
  // eslint-disable-next-line no-unused-vars
  const [fileCount, setFileCount] = useState(0);

  const [ssceFormRow, setSsceFormRow] = useState([
    { examYear: '', examNo: '', examType: '' },
  ]);

  const [firstSittingForm, setFirstSittingForm] = useState({});
  const [secondSittingForm, setSecondSittingForm] = useState({});

  const { loadAssert } = CloudAssert();

  // Pipeline/Stage handling
  const currentPipeline = metadata?.pipelineProgress || 2;
  const [currentPage, setCurrentPage] = useState<number>(currentPipeline);

  const [currentDocument, setCurrentDocument] = useState<string>('');

  const [displayForm, setDisplayForm] = useState(false);
  const [validationData, setValidationData] = useState<any>(null);
  const [finalSubmissionLoad, setFinalSubmissionLoad] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(false);

  // Form hooks
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<any>();

  const selectedValue = watch('skillTitle');
  const subArray: any = Object.keys(loadAssert?.subjects?.examSubject || '');
  const isNimcPhotoValid = nimcData?.photo?.length > 10;

  // Manifest
  const manifestData = {
    nin: resData?.nimc?.nin,
    workflow: 'formSubmission'
  };
  const { manifestInfo, askForManifest } = useManifest(manifestData);

  // Validation Checks
  const isFormValid = manifestInfo ? Object.keys(manifestInfo).every((key: any) => manifestInfo?.[key]?.upload) : false;
  const isProfessional = ['professionalCertificate'].every((key: any) => manifestInfo?.[key]?.upload);

  // Effects
  useEffect(() => {
    allowRegistration();
    setNinValidated(true);

    if (resData) {
      setRadioForm(resData.formData.candidateRole);
      setNimcData({ ...resData.nimc, photo });
      if (metadata?.pipelineProgress === 3) {
        setCurrentPage(4);
      }
    }
  }, []);

  useEffect(() => {
    if (nimcData) {
      const isMock = dataset?.nimc?.mockID;
      const dataSource = isMock ? dataset.formData : nimcData;

      const rawDob = isMock ? dataSource.dob : dataSource.dateOfBirth;
      const formatDOB = rawDob ? rawDob.split('-').reverse().join('-') : '';

      setValue('surname', isMock ? dataSource.surname : dataSource.lastName, { shouldValidate: true });
      setValue('firstname', dataSource.firstName, { shouldValidate: true });
      setValue('dob', formatDOB, { shouldValidate: true });
      if (formatDOB) {
        setValue('age', getAgeFromYear(new Date(formatDOB)), { shouldValidate: true });
      }
      setValue('gender', dataSource.gender, { shouldValidate: true });
      setValue('phoneno', dataSource.phoneno, { shouldValidate: true });
      setValue('email', dataSource.email, { shouldValidate: true });
    }
  }, [nimcData, setValue, dataset]);

  useEffect(() => {
    if (selectedValue !== '' || radioForm === 'gdc') {
      setDisplayForm(true);
    } else {
      setDisplayForm(false);
    }
  }, [selectedValue, radioForm]);

  // Methods
  const allowRegistration = () => {
    if (!AllowAccessToApplication()) {
      navigate('/');
    }
  };

  const throwError = (mess: string = 'There was an error submitting application!') => {
    message.error({
      content: mess,
      style: { zIndex: '200000' },
    });
  };

  const changeConsent = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setConsent(e.target.checked);
    },
    []
  );

  const changeCurrentDocument = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentDocument(e.target.value);
    setDocumentError('');
  };

  const removeDocument = useCallback(
    (type: string) => {
      setDocuments(prev => ({ ...prev, [type]: '' }));
      setFormData(prev => ({ ...prev, [type]: false }));
    },
    []
  );

  const getDocumentsUploadProgress = useCallback(() => {
    const doc = documents as DocumentsType;
    let successDoc = 0;
    let wasscProgress = 0;

    Object.keys(doc).forEach((key: string) => {
      if (doc[key] !== '') {
        if (['waec', 'neco', 'gce', 'nabteb'].includes(key)) {
          if (wasscProgress === 0) {
            wasscProgress++;
            successDoc++;
          }
        } else {
          successDoc++;
        }
      }
    });

    if (successDoc > 0) {
      const denominator = radioForm === 'gdc' ? 3 : 4;
      return Math.round((successDoc / denominator) * 100);
    }
    return 0;
  }, [documents, radioForm]);

  const fileUploader = async (data: {
    document: any;
    nin: number;

    documentType: string;
  }) => {
    const headers = {
      'Content-Type': 'multipart/form-data',
      Accept: 'application/json',
    };

    try {
      const res = await axios.post(
        `${BASE_URL_ADDR}/recruitment/receive-upload-credentials`,
        { ...data, profileFetchMode: 2 },
        { headers }
      );

      message.success({
        content: 'File upload successful',
        style: { zIndex: '200000' },
      });

      if (data.documentType === 'passport') {
        askForManifest();
        const photoUrl = res.data.url;
        updateNimcPhoto(photoUrl);
      } else {
        askForManifest();
      }
    } catch (error: any) {
      throwError(error.response?.data?.message);
    }
  };

  const changeNewPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    const file = e.target.files[0];
    const fileSize = file.size;
    const maxPassport = 200 * 1024;
    const maxLimit = 2 * 1024 * 1024;

    if (currentDocument === 'passport' && fileSize > maxPassport) {
      toast.error('passport larger than 200kb, make sure the passport fit this size and try again');
      return;
    }
    if (fileSize > maxLimit) {
      toast.error('dear applicant, document size is larger than 2MB, make sure the passport fit this size and try again');
      return;
    }

    const dataFileUp = {
      document: file,
      nin: resData.nimc.nin,
      documentType: currentDocument,
    };

    await fileUploader(dataFileUp);
  };

  const updateNimcPhoto = (photoUrl: string) => {
    setNimcData((prev: any) => ({ ...prev, photo: photoUrl }));
  };

  const onDrop = async (acceptedFiles: any[]) => {
    const requiredFileTypes = ['jpg', 'png', 'jpeg', 'pdf'];
    const validate = validateType(acceptedFiles[0].type, requiredFileTypes);

    if (validate) {
      setFileCount(prev => prev + 1);
      const file = acceptedFiles[0];
      const isSSCE = ['neco', 'waec', 'gce', 'nabteb'].includes(currentDocument);

      let docKey = currentDocument;
      if (isSSCE) {
        docKey = documents[currentDocument] !== '' ? 'ssce1' : 'ssce';
      }

      setDocuments(prev => ({
        ...prev,
        [docKey]: file,
      }));

      if (!isSSCE && documents[currentDocument] === '') {
        setFormData(prev => ({
          ...prev,
          [currentDocument]: true,
        }));
      }

      const dataFileUp = {
        document: file,
        nin: resData.nimc.nin,
        documentType: docKey,
      };
      await fileUploader(dataFileUp);
    } else {
      setDocumentError(`Please, upload either of these file types; ${requiredFileTypes.join(', ')}`);
    }
  };

  const validateType = (type: string, acceptedTypes: string[]) => {
    const trailSlashIndex = type.indexOf('/');
    const fileType = type.slice(trailSlashIndex + 1);
    return acceptedTypes.includes(fileType);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const onSubmit = async (data: any) => {
    const newData = {
      ...data,
      firstSitting: { ...firstSittingForm },
      secondSitting: { ...secondSittingForm },
      sscedetails: ssceFormRow,
      institutionDetails: institutionRow,
      nin: resData.nimc.nin,
      firstSittingRegNo: '',
      secondSittingRegNo: '',
    };

    setDocumentError('');
    setResultError('');
    setExamNoError('');

    if (!isNimcPhotoValid) {
      throwError('Please, upload a photo');
      return;
    }

    setLoading(prev => ({ ...prev, submit: true, step1: true }));

    const isValidNames = () => {
      const hasFirstName = newData.firstname !== '';
      // console.log('nimcData', nimcData);
      if (dataset?.nimc?.mockID !== undefined) {
        return hasFirstName;
      } else {
        const matchesNimc = nimcData.firstName && newData.firstname === nimcData.firstName && newData.surname === nimcData.lastName;
        return hasFirstName && matchesNimc;
      }
    };

    // const isValidProfessional = () => {
    //   const hasNames = (nimcData.firstName !== "" || newData.firstname !== '') && (newData.surname !== '' && nimcData.lastName !== '');
    //   return hasNames;
    // }
    console.log('isFormValid', isFormValid, 'isProfessional', isProfessional);

    let canSubmit = false;
    let errorMessage = '';

    if (resData.formData.candidateRole === 'gdc') {
      if (isFormValid) {
        // console.log('Validating names for GDC');
        if (isValidNames()) {
          // console.log('Names valid, can submit');
          canSubmit = true;
        } else {
          // console.log('Names invalid for GDC');
          // Logic from original code implies silent fail or toast error
          toast.error('Please make sure the first name and last name are the same as on your nin');
        }
      } else {
        errorMessage = 'Please make sure that all necessary document are uploaded';
      }
    } else {
      // Specialist
      if (isFormValid) {
        if (isValidNames()) {
          // console.log('Names valid for Specialist, can submit');
          canSubmit = true;
        } else {
          errorMessage = 'Please make sure the names field are entered correctly';
        }
      } else {
        errorMessage = 'Please make sure that all necessary document are uploaded';
      }
    }

    if (canSubmit) {
      // console.log('Submitting application with data:', newData);
      await submitApplication({ formData: newData });
    } else {
      if (errorMessage) toast.error(errorMessage);
      setLoading(prev => ({ ...prev, submit: false }));
    }
  };

  const submitApplication = async (data: { formData: any }) => {
    setLoading(prev => ({ ...prev, submit: true }));
    try {
      const response = await applicantsService.validate(resData.nimc.nin, data);
      if (response.data) {
        // await applicantsService.crsync(resData.nimc.nin);
        setValidationData(response.data);
        setCurrentPage(4);
      }
    } catch (error: any) {
      if (error?.response?.data?.display === 'html') {
        setBoxErrorMessage(error?.response?.data?.message);
      } else {
        throwError(error?.response?.data?.message);
      }
    } finally {
      setLoading(prev => ({ ...prev, submit: false }));
    }
  };

  const finalSubmissionForm = async () => {
    const nnin = validationData?.dataset?.nin || resData.nimc.nin;
    const uuidRecord = validationData?.dataset?.uuid || uuid;
    const todayDate = new Date().toLocaleDateString('en-GB').replace(/\//g, '-');

    const base64Encode = btoa(nnin);
    const digest = `${uuidRecord}+${nnin}+${todayDate}`;
    const sha256d = SHA256(digest).toString();

    setFinalSubmissionLoad(true);
    try {
      const response = await applicantsService.finalSubmission({ hashNIN: base64Encode, hashSubmission: sha256d });
      if (response) {
        setSubmissionStatus(true);
        // Clear state but we can't really clear location.state directly without navigation
        setTimeout(() => {
          navigate('/print-slip');
          setSubmissionStatus(false);
        }, 2000);
      }
    } catch (error: any) {
      toast.error(`Error submitting: ${error.response?.data?.message || 'Unknown error'}`);
    } finally {
      setFinalSubmissionLoad(false);
    }
  };

  const changeFormData = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    const check = name.substring(0, 4);

    if (check === 'grad' || check === 'subj') {
      const prevFormData = { ...formData } as any;
      const sitt = prevFormData[currentSitting];
      sitt[name] = value;
      setFormData({ ...prevFormData, [currentSitting]: sitt });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleCertificateChange = (index: number, event: React.ChangeEvent<any>) => {
    const values = [...institutionRow];
    const updatedValue = event.target.name as keyof InstitutionRow;
    // @ts-ignore
    values[index][updatedValue] = event.target.value;
    setInstitutionRow(values);
  };

  // SSCE Form Rows
  const handleAddFormRow = (e: any) => {
    e.preventDefault();
    setSsceFormRow([...ssceFormRow, { examNo: '', examType: '', examYear: '' }]);
  };

  const handleRemoveFormRow = (index: number) => {
    const values = [...ssceFormRow];
    values.splice(index, 1);
    setSsceFormRow(values);
  };

  const handleFormRowInputChange = (index: number, event: any) => {
    const values = [...ssceFormRow];
    const updatedValue = event.target.name as keyof typeof values[0];
    values[index][updatedValue] = event.target.value;
    setSsceFormRow(values);
  };

  const validateFormRow = () => {
    return !!(ssceFormRow[0].examType && ssceFormRow[0].examNo && ssceFormRow[0].examYear);
  };

  const handleFirstSittingChange = (evt: any) => {
    // console.log(evt.target.name)
    setFirstSittingForm({ ...firstSittingForm, [evt.target.name]: evt.target.value });
  };

  const handleSecondSittingChange = (evt: any) => {
    setSecondSittingForm({ ...secondSittingForm, [evt.target.name]: evt.target.value });
  };

  // Render
  return (
    <Fragment>
      <div className='main-app'>
        <main role='main' className='container'>
          <form onSubmit={handleSubmit(onSubmit)}>
            {boxErrorMessage && (
              <RegistrationFail
                message={boxErrorMessage}
                clearErrorBox={() => setBoxErrorMessage('')}
              />
            )}

            {ninValidated && !boxErrorMessage && (
              <div className='py-2'>
                <div className='card border-0 rounded-3 w-100 rounded'>
                  <div className='section-container sub-hero_application rounded-top py-5'>
                    <div className='section-item-container'></div>
                    <StageTag currentCount={currentPage} />
                  </div>

                  {currentPage === 2 && (
                    <ApplicationStage2
                      boxErrorMessage={boxErrorMessage}
                      trigger={trigger}
                      ninValidated={ninValidated}
                      displayForm={displayForm}
                      newPhotoInputRef={newPhotoInputRef}
                      changeNewPhoto={changeNewPhoto}
                      nimcData={nimcData}
                      Base64AppendString={Base64AppendString}
                      register={register}
                      setValue={setValue}
                      errors={errors}
                      setCurrentDocument={setCurrentDocument}
                      currentPage={currentPage}
                      changeFormData={changeFormData}
                      states={states}
                      formData={formData}
                      setCurrentPage={setCurrentPage}
                      lgas={lgas}
                      loadAssert={loadAssert}
                      manifestInfo={manifestInfo}
                    />
                  )}

                  {currentPage === 3 && (
                    <ApplicationStage4
                      boxErrorMessage={boxErrorMessage}
                      ninValidated={ninValidated}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      displayForm={displayForm}
                      register={register}
                      errors={errors}
                      changeFormData={changeFormData}
                      formData={formData}
                      states={states}
                      loading={loading}
                      changeConsent={changeConsent}
                      documentsMapper={documentsMapper}
                      lgas={lgas}
                      ssceFormRow={ssceFormRow}
                      handleAddFormRow={handleAddFormRow}
                      handleRemoveFormRow={handleRemoveFormRow}
                      handleFormRowInputChange={handleFormRowInputChange}
                      validateFormRow={validateFormRow}
                      subArray={subArray}
                      handleFirstSittingChange={handleFirstSittingChange}
                      handleSecondSittingChange={handleSecondSittingChange}
                      ssceLoading={ssceLoading}
                      consent={consent}
                      documentError={documentError}
                      resultError={resultError}
                      examNoError={examNoError}
                      setCurrentSitting={setCurrentSitting}
                      changeCurrentDocument={changeCurrentDocument}
                      removeDocument={removeDocument}
                      getDocumentsUploadProgress={getDocumentsUploadProgress}
                      radioForm={radioForm}
                      getRootProps={getRootProps}
                      getInputProps={getInputProps}
                      isDragActive={isDragActive}
                      currentSitting={currentSitting}
                      ssce={ssce}
                      manifestInfo={manifestInfo}
                      sittings={sittings}
                      setSittings={setSittings}
                      documents={documents as any}
                      handleCertificateChange={handleCertificateChange}
                      loadAssert={loadAssert}
                      currentDocument={currentDocument}
                      institutionRow={institutionRow}
                    />
                  )}

                  {currentPage === 4 && (
                    <ReviewStage
                      setCurrentPage={setCurrentPage}
                      currentPage={currentPage}
                      validationData={validationData}
                      finalSubmissionForm={finalSubmissionForm}
                      finalSubmissionLoad={finalSubmissionLoad}
                      submissionStatus={submissionStatus}
                      nin={resData?.nimc?.nin}
                      // manifestInfo={manifestInfo}
                    />
                  )}
                </div>
              </div>
            )}
          </form>
        </main>
      </div>
      <Toaster />
    </Fragment>
  );
};

export default Application;
